import { COUNTRIES } from "@breenova/domain";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  FieldError,
  FieldHint,
  Input,
  Label,
  Picker,
} from "@breenova/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { api } from "@/lib/api";
import { AuthLayout } from "@/routes/auth-layout";

/** Mirrors the server's policy, so a short password is caught before the round trip. */
const MINIMUM_PASSWORD_LENGTH = 12;

interface FederationOption {
  id: string;
  code: string;
  name: string;
  countryCode?: string | null;
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [federationId, setFederationId] = useState("");
  const [ringCode, setRingCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Anonymous endpoint — the form needs these before anyone has an account.
  const federations = useQuery({
    queryKey: ["registration-options"],
    queryFn: async (): Promise<FederationOption[]> => {
      const { data, error: failed } = await api.GET("/api/v1/auth/registration-options");

      if (failed || !data) {
        throw new Error("Could not load federations");
      }

      return (data.federations ?? []) as FederationOption[];
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  const passwordTooShort = password.length > 0 && password.length < MINIMUM_PASSWORD_LENGTH;

  // Federations are mostly national, so showing the ones for the chosen country first
  // saves scrolling past ten irrelevant bodies. Global ones stay visible.
  const relevantFederations = (federations.data ?? []).filter(
    (federation) =>
      !countryCode || !federation.countryCode || federation.countryCode === countryCode,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setError(`Your password needs at least ${MINIMUM_PASSWORD_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);

    try {
      await register({
        email: email.trim(),
        password,
        displayName: displayName.trim(),
        countryCode,
        federationId: federationId || null,
        ringCode: ringCode.trim() || null,
      });

      await navigate("/", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the account.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Create your account" subtitle="One account holds one aviary's records.">
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-md" noValidate>
            {error ? <Alert tone="error">{error}</Alert> : null}

            <Field>
              <Label htmlFor="displayName">Name</Label>
              <Input
                id="displayName"
                name="displayName"
                autoComplete="name"
                required
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </Field>

            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                inputMode="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MINIMUM_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={passwordTooShort ? true : undefined}
                aria-describedby="password-hint"
              />
              {passwordTooShort ? (
                <FieldError>At least {MINIMUM_PASSWORD_LENGTH} characters.</FieldError>
              ) : (
                <FieldHint id="password-hint">
                  At least {MINIMUM_PASSWORD_LENGTH} characters. A few ordinary words beat
                  one cryptic one, and they are far easier to type in a cold birdroom.
                </FieldHint>
              )}
            </Field>

            <Field>
              <Label>Country</Label>
              <Picker
                id="countryCode"
                label="Country"
                placeholder="Choose a country…"
                value={countryCode}
                onChange={setCountryCode}
                options={COUNTRIES.map((country) => ({
                  value: country.code,
                  label: country.name,
                }))}
              />
            </Field>

            <Field>
              <Label>Federation</Label>
              <Picker
                id="federationId"
                label="Federation"
                disabled={federations.isPending}
                emptyLabel="No federation for that country"
                value={federationId}
                onChange={setFederationId}
                options={[
                  { value: "", label: "Not a member / add later" },
                  ...relevantFederations.map((federation) => ({
                    value: federation.id,
                    label: federation.name,
                    hint: federation.code,
                  })),
                ]}
              />
              <FieldHint>Optional. It sets the ring format your birds are checked against.</FieldHint>
            </Field>

            <Field>
              <Label htmlFor="ringCode">Ring code</Label>
              <Input
                id="ringCode"
                name="ringCode"
                placeholder="NL-1234"
                value={ringCode}
                onChange={(event) => setRingCode(event.target.value)}
              />
              <FieldHint>Optional. The breeder code stamped on your closed rings.</FieldHint>
            </Field>

            <Button type="submit" block disabled={submitting}>
              {submitting ? "Creating your account…" : "Create account"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-center text-body text-on-surface-variant">
        Already have an account?{" "}
        <Link to="/login" className="text-primary underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
